import {
  Color3,
  Material,
  MaterialPluginBase,
  Matrix,
  Mesh,
  Quaternion,
  Scene,
  ShaderLanguage,
  StandardMaterial,
  VertexData,
  Vector3,
  type AbstractEngine,
  type AbstractMesh,
  type MaterialDefines,
  type SubMesh,
  type UniformBuffer,
} from '@babylonjs/core';
import type { ChunkManager } from './ChunkManager.js';

/** Radius (m) of the grass disc around the car. Clumps fade out near the edge. */
const RADIUS = 50;
/** Spacing (m) of the clump grid — one candidate clump per cell. Larger = more spread out. */
const CLUMP_SPACING = 5;
/** Fraction of clump cells that actually grow grass. Lower = more sporadic, bare ground between. */
const CLUMP_DENSITY = 0.38;
/** Max tufts emitted per active clump; the actual count is a hashed 3..MAX. */
const TUFTS_PER_CLUMP = 5;
/** Tufts of a clump scatter within this radius (m) of the clump center — small so the 3-5 blades
 *  read as one tight tuft, not a spread-out patch. */
const CLUMP_RADIUS = 0.8;
/** Keep clumps this far (m) clear of road/path edges. Roads render along a smoothed centerline
 *  with ~1.2 m shoulders/sidewalks that extend past the bare OSM half-width, so without a margin
 *  blades poke through the visual road on curves and shoulders. */
const ROAD_CLEARANCE = 2.2;
/** Recompute the field only when the car has moved at least this far (m) since the last
 *  rebuild — same "skip tiny moves" guard the billboard trees use. */
const REBUILD_MOVE_THRESHOLD = CLUMP_SPACING * 0.5;
/** Tuft geometry. Three triangle blades fanned around Y read as a tuft from any angle. */
const BLADE_HEIGHT = 0.225;
const BLADE_WIDTH = 0.065;

/** Grass tint extremes — base to tip. The per-instance cover color multiplies these so blades
 *  match the terrain underneath. The BASE is kept close to the terrain green on purpose: a dark
 *  base contrasts the lighter ground enough to trip the anime color-edge ink, which drew a
 *  distracting black ring around each tuft. Matching the base keeps that edge below threshold,
 *  while the brighter, more saturated TIP lets the blade still read above the flat terrain. */
const BLADE_BASE = new Color3(0.5, 0.66, 0.4);
const BLADE_TIP = new Color3(0.58, 0.82, 0.42);

/**
 * A single global grass field that follows the car. Maintains a fixed pool of thin-instanced
 * tufts arranged in a jittered grid disc; on each rebuild it re-samples terrain height, cover
 * color and surface type per tuft, hiding any that land on roads/paths/water/sand/rock and
 * tinting the rest to match the ground. Mirrors the thin-instance + zero-scale-hide pattern
 * used by the tree batches in trees.ts.
 */
export class GrassField {
  private mesh: Mesh;
  private chunks: ChunkManager;
  private wind: GrassWindPlugin;
  /** Per-clump integer cell offset from the anchor cell, plus its edge-fade weight. The actual
   *  world position of the clump that lands in a slot is derived from a hash of its world cell
   *  coords (see update), so clumps stay nailed to the ground as the anchor moves. Each clump
   *  owns TUFTS_PER_CLUMP consecutive instance slots. */
  private cellX: Int32Array;
  private cellZ: Int32Array;
  private edgeFade: Float32Array;
  private matrices: Float32Array;
  /** Per-instance RGBA tint buffer (kept by reference; mutated in place each rebuild). */
  private colorBuf: Float32Array;
  /** Number of candidate clumps (disc cells). Total instances = clumpCount * TUFTS_PER_CLUMP. */
  private clumpCount: number;
  private count: number;
  private lastAnchorX = Number.POSITIVE_INFINITY;
  private lastAnchorZ = Number.POSITIVE_INFINITY;
  private enabled = true;

  // Scratch — reused each rebuild to avoid per-frame allocation.
  private readonly tmpQ = new Quaternion();
  private readonly tmpM = new Matrix();
  private readonly tmpS = new Vector3();
  private readonly tmpPos = new Vector3();
  private readonly zero = new Float32Array(16);

  constructor(scene: Scene, chunks: ChunkManager) {
    this.chunks = chunks;

    // -- Build the disc of clump cells (radius RADIUS); each owns TUFTS_PER_CLUMP slots --
    const cellX: number[] = [];
    const cellZ: number[] = [];
    const edge: number[] = [];
    const cells = Math.ceil(RADIUS / CLUMP_SPACING);
    for (let icz = -cells; icz <= cells; icz++) {
      for (let icx = -cells; icx <= cells; icx++) {
        const cxm = icx * CLUMP_SPACING;
        const czm = icz * CLUMP_SPACING;
        const d = Math.sqrt(cxm * cxm + czm * czm);
        if (d > RADIUS) continue;
        cellX.push(icx);
        cellZ.push(icz);
        // 1 inside 0.75R, ramps to 0 at the rim so clumps pop in/out softly.
        edge.push(smoothstep(RADIUS, RADIUS * 0.75, d));
      }
    }
    this.clumpCount = cellX.length;
    this.count = this.clumpCount * TUFTS_PER_CLUMP;
    this.cellX = new Int32Array(cellX);
    this.cellZ = new Int32Array(cellZ);
    this.edgeFade = new Float32Array(edge);
    this.matrices = new Float32Array(this.count * 16);
    this.colorBuf = new Float32Array(this.count * 4).fill(1);

    // -- Base tuft mesh --
    this.mesh = buildTuftMesh(scene);
    const mat = new StandardMaterial('grass-mat', scene);
    mat.specularColor = new Color3(0, 0, 0);
    mat.backFaceCulling = false;
    mat.diffuseColor = new Color3(1, 1, 1);
    this.wind = new GrassWindPlugin(mat);
    this.wind.enabled = true;
    this.mesh.material = mat;
    this.mesh.alwaysSelectAsActiveMesh = true;
    this.mesh.isPickable = false;

    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    // Per-instance color (RGBA) tints each tuft; multiplied with the base→tip vertex gradient.
    this.mesh.thinInstanceSetBuffer('color', this.colorBuf, 4, false);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.mesh.setEnabled(on);
  }

  /** Advance the wind animation. `timeSec` is monotonically increasing seconds. */
  setTime(timeSec: number): void {
    this.wind.time = timeSec;
  }

  /**
   * Reposition + re-sample the field around (carX, carZ). Cheap no-op when the car hasn't
   * crossed half a grid cell since the last rebuild.
   */
  update(carX: number, carZ: number): void {
    if (!this.enabled) return;
    const dx = carX - this.lastAnchorX;
    const dz = carZ - this.lastAnchorZ;
    if (dx * dx + dz * dz < REBUILD_MOVE_THRESHOLD * REBUILD_MOVE_THRESHOLD) return;
    // Anchor cell the car currently sits in. Each clump maps to (anchorCell + cellOffset); its
    // world position/activation/tuft layout come from a hash of that world cell, so a clump
    // staying in range keeps its exact world position across rebuilds and never swims.
    const anchorCellX = Math.round(carX / CLUMP_SPACING);
    const anchorCellZ = Math.round(carZ / CLUMP_SPACING);

    let placed = 0;
    for (let k = 0; k < this.clumpCount; k++) {
      const cx = anchorCellX + this.cellX[k]!;
      const cz = anchorCellZ + this.cellZ[k]!;
      const base = k * TUFTS_PER_CLUMP;
      const edgeF = this.edgeFade[k]!;
      // Clump-level randoms: activation, center jitter x/z, tuft count.
      let h = hashCell(cx, cz);
      const active = (h & 0xffff) / 0xffff;
      h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
      const j1 = (h & 0xffff) / 0xffff;
      h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
      const j2 = (h & 0xffff) / 0xffff;
      h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
      const cntR = (h & 0xffff) / 0xffff;

      // Inactive (sporadic) clump or past the disc rim → hide all of its slots.
      if (active > CLUMP_DENSITY || edgeF < 0.05) {
        this.matrices.fill(0, base * 16, (base + TUFTS_PER_CLUMP) * 16);
        continue;
      }
      const ccx = cx * CLUMP_SPACING + (j1 - 0.5) * CLUMP_SPACING * 0.6;
      const ccz = cz * CLUMP_SPACING + (j2 - 0.5) * CLUMP_SPACING * 0.6;
      // Sample once at the clump center — the 3-5 tufts sit within <1 m so one lookup is enough.
      const cover = this.chunks.sampleCoverColor(ccx, ccz);
      const y = this.chunks.sampleHeight(ccx, ccz);
      const isGreen = cover !== null && cover[1] > cover[0] && cover[1] > cover[2] && cover[1] > 0.28;
      let surface: string | null = null;
      if (isGreen && y !== null) surface = this.chunks.classifySurface(ccx, ccz, ROAD_CLEARANCE);
      if (!isGreen || y === null || surface === 'road' || surface === 'path') {
        this.matrices.fill(0, base * 16, (base + TUFTS_PER_CLUMP) * 16);
        continue;
      }
      const tintR = 0.6 + cover![0] * 0.6;
      const tintG = 0.7 + cover![1] * 0.5;
      const tintB = 0.6 + cover![2] * 0.6;
      const tuftCount = 3 + Math.floor(cntR * (TUFTS_PER_CLUMP - 2)); // 3..TUFTS_PER_CLUMP
      for (let t = 0; t < TUFTS_PER_CLUMP; t++) {
        const slot = base + t;
        if (t >= tuftCount) {
          this.matrices.set(this.zero, slot * 16);
          continue;
        }
        // Per-tuft randoms (advance the same deterministic stream): offset angle/radius, yaw, scale.
        h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
        const a = (h & 0xffff) / 0xffff;
        h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
        const rr = (h & 0xffff) / 0xffff;
        h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
        const yaw = (h & 0xffff) / 0xffff;
        h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
        const sc = (h & 0xffff) / 0xffff;
        const ang = a * Math.PI * 2;
        const rad = Math.sqrt(rr) * CLUMP_RADIUS;
        const tx = ccx + Math.cos(ang) * rad;
        const tz = ccz + Math.sin(ang) * rad;
        const s = (0.75 + sc * 0.5) * edgeF;
        Quaternion.RotationAxisToRef(Vector3.UpReadOnly, yaw * Math.PI * 2, this.tmpQ);
        this.tmpS.set(s, s, s);
        this.tmpPos.set(tx, y, tz);
        Matrix.ComposeToRef(this.tmpS, this.tmpQ, this.tmpPos, this.tmpM);
        this.tmpM.copyToArray(this.matrices, slot * 16);
        this.colorBuf[slot * 4] = tintR;
        this.colorBuf[slot * 4 + 1] = tintG;
        this.colorBuf[slot * 4 + 2] = tintB;
        this.colorBuf[slot * 4 + 3] = 1;
        placed++;
      }
    }
    this.mesh.thinInstanceBufferUpdated('matrix');
    this.mesh.thinInstanceBufferUpdated('color');

    // Latch the anchor only once a rebuild actually placed blades. Before the spawn chunk's
    // cover/height data is ready every instance is hidden; leaving the anchor un-latched makes
    // the next frame retry, so a car that spawns stationary still gets grass once data arrives.
    if (placed > 0) {
      this.lastAnchorX = carX;
      this.lastAnchorZ = carZ;
    }
  }

  dispose(): void {
    const mat = this.mesh.material;
    this.mesh.dispose(false, false);
    if (mat) mat.dispose();
  }
}

/** Deterministic 32-bit hash of an integer world-cell coordinate. Same cell → same value,
 *  so a blade keeps its world position/yaw/scale no matter which slot currently holds it. */
function hashCell(cx: number, cz: number): number {
  let h = (Math.imul(cx | 0, 0x27d4eb2d) ^ Math.imul(cz | 0, 0x165667b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  return h >>> 0;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0 || 1e-9)));
  return t * t * (3 - 2 * t);
}

/** Three triangle blades fanned 60° apart around Y. Base verts are darker (BLADE_BASE),
 *  the apex is lighter (BLADE_TIP) via vertex colors. UV.y encodes height (0 base → 1 tip)
 *  so the wind plugin can sway only the upper part. */
function buildTuftMesh(scene: Scene): Mesh {
  const positions: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const hw = BLADE_WIDTH * 0.5;
  for (let b = 0; b < 3; b++) {
    const a = (b / 3) * Math.PI;
    const dx = Math.cos(a);
    const dz = Math.sin(a);
    const base = positions.length / 3;
    // Two base verts and one apex.
    positions.push(-dx * hw, 0, -dz * hw);
    positions.push(dx * hw, 0, dz * hw);
    positions.push(0, BLADE_HEIGHT, 0);
    // Normal: blade roughly faces up so it catches the sun/ambient like the ground.
    for (let k = 0; k < 3; k++) normals.push(0, 1, 0);
    uvs.push(0, 0, 1, 0, 0.5, 1);
    colors.push(
      BLADE_BASE.r, BLADE_BASE.g, BLADE_BASE.b, 1,
      BLADE_BASE.r, BLADE_BASE.g, BLADE_BASE.b, 1,
      BLADE_TIP.r, BLADE_TIP.g, BLADE_TIP.b, 1,
    );
    indices.push(base, base + 1, base + 2);
  }
  const mesh = new Mesh('grass-tuft', scene);
  const vd = new VertexData();
  vd.positions = new Float32Array(positions);
  vd.indices = new Uint32Array(indices);
  vd.normals = new Float32Array(normals);
  vd.uvs = new Float32Array(uvs);
  vd.colors = new Float32Array(colors);
  vd.applyToMesh(mesh, false);
  return mesh;
}

/**
 * Lightweight cartoon wind. Sways blade tops (UV.y > 0) in local X/Z by a small sine of time.
 * A per-vertex spatial term keeps a tuft's blades from moving in perfect lockstep. Amplitude
 * is deliberately small (~few cm).
 */
class GrassWindPlugin extends MaterialPluginBase {
  enabled = false;
  time = 0;

  constructor(material: Material) {
    super(material, 'GrassWind', 260, { GRASS_WIND: false });
  }

  override isCompatible(_lang: ShaderLanguage): boolean {
    return true;
  }

  override prepareDefines(defines: MaterialDefines, _scene: Scene, _mesh: AbstractMesh): void {
    (defines as unknown as Record<string, boolean>).GRASS_WIND = this.enabled;
  }

  override getAttributes(attributes: string[]): void {
    // The wind sway reads uv.y; force the attribute since a textureless StandardMaterial
    // wouldn't otherwise declare it in the vertex shader.
    attributes.push('uv');
  }

  override getUniforms(): { ubo?: Array<{ name: string; size?: number; type?: string }> } {
    return { ubo: [{ name: 'grassWindTime', size: 1, type: 'float' }] };
  }

  override bindForSubMesh(
    ubo: UniformBuffer,
    _scene: Scene,
    _engine: AbstractEngine,
    _subMesh: SubMesh,
  ): void {
    if (!this.enabled) return;
    ubo.updateFloat('grassWindTime', this.time);
  }

  override getCustomCode(
    shaderType: string,
    shaderLanguage?: ShaderLanguage,
  ): { [pointName: string]: string } | null {
    if (shaderType !== 'vertex') return null;
    if (shaderLanguage === ShaderLanguage.WGSL) {
      return {
        CUSTOM_VERTEX_UPDATE_POSITION: `
          #ifdef GRASS_WIND
            let gw = vertexInputs.uv.y;
            let phase = uniforms.grassWindTime * 1.8 + positionUpdated.x * 3.0 + positionUpdated.z * 3.0;
            positionUpdated.x = positionUpdated.x + sin(phase) * 0.05 * gw;
            positionUpdated.z = positionUpdated.z + cos(phase * 0.7) * 0.03 * gw;
          #endif
        `,
      };
    }
    return {
      CUSTOM_VERTEX_UPDATE_POSITION: `
        #ifdef GRASS_WIND
          float gw = uv.y;
          float phase = grassWindTime * 1.8 + positionUpdated.x * 3.0 + positionUpdated.z * 3.0;
          positionUpdated.x += sin(phase) * 0.05 * gw;
          positionUpdated.z += cos(phase * 0.7) * 0.03 * gw;
        #endif
      `,
    };
  }
}
